import React, { useCallback, useMemo, useEffect } from "react";
import { BookingStatus, TransportData, TripTransport } from "@/types/trip";
import { Trip } from "@/types/trip";
import { format } from "date-fns";
import useTransport from "@/stores/tripBuilder/useTransport";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useBookingStatus } from "@/hooks/useBookingStatus";
import { calculateDuration, formatDuration } from "@/lib/transport";
import MapView from "./MapView";
import UserFlight from "./UserFlight";

interface TransportContainerProps {
  selectedFlight: TripTransport | null;
  setSelectedFlight: (flight: TripTransport | null) => void;
  deleteFlightFromTrip: (trip: Trip, transportData: TransportData) => void;
  setFlightModalOpen: (isOpen: boolean) => void;
}

const TransportContainer: React.FC<TransportContainerProps> = ({
  selectedFlight,
  setSelectedFlight,
  deleteFlightFromTrip,
  setFlightModalOpen,
}) => {
  const { tripData } = useTripStore();
  const { transportData } = useTransport();
  const { updateBookingStatus, isUpdating } = useBookingStatus();

  // Add effect to handle modal state and flight selection with proper cleanup
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedFlight(null);
      }
    };

    window.addEventListener("keydown", handleEscapeKey);

    return () => {
      window.removeEventListener("keydown", handleEscapeKey);
      setSelectedFlight(null);
    };
  }, [setSelectedFlight]);

  const relevantAddedFlight = useMemo(() => {
    if (
      !tripData ||
      !transportData.locationAbove ||
      !transportData.locationBelow
    ) {
      return null;
    }

    return tripData.transport.find(
      (transport: TripTransport) =>
        transport.fromLocationId === transportData.locationAbove?.id &&
        transport.toLocationId === transportData.locationBelow?.id
    );
  }, [tripData, transportData.locationAbove, transportData.locationBelow]);

  const onDeleteFlight = useCallback(() => {
    if (!tripData || !transportData) return;
    deleteFlightFromTrip(tripData, transportData);
  }, [deleteFlightFromTrip, tripData, transportData]);

  // Memoize the status change handler
  const handleStatusChange = useCallback(
    (newStatus: string) => {
      if (relevantAddedFlight) {
        updateBookingStatus(
          "transport",
          relevantAddedFlight.id,
          newStatus as BookingStatus
        );
      }
    },
    [relevantAddedFlight, updateBookingStatus]
  );

  if (!tripData)
    return (
      <h1 className="text-neutral-800 text-2xl font-medium">Loading...</h1>
    );

  return (
    <section className="w-full h-[calc(100vh-4rem)] flex flex-col px-2.5 py-1.5">
      {/* Map View - Fill remaining height */}
      <div className="flex-1 min-h-0 mb-4">
        <MapView
          selectedFlight={selectedFlight}
          relevantAddedFlight={relevantAddedFlight || null}
          fromLocation={transportData.locationAbove!}
          toLocation={transportData.locationBelow!}
        />
      </div>

      <div className="h-[300px] bg-neutral-100 rounded-xl p-2 flex flex-col gap-2">
        {/* General Overview - Fixed height */}
        <div className="h-[80px] bg-white w-full rounded-xl px-3 flex items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center">
              <i className="fa-solid fa-passport text-indigo-600 text-sm"></i>
            </div>
            <div className="flex flex-col gap-0.5">
              <h2 className="text-neutral-800 text-base font-medium">
                {transportData.locationAbove?.name} to{" "}
                {transportData.locationBelow?.name}
              </h2>
              <div className="flex items-center gap-2 text-xs text-neutral-500">
                <i className="fa-regular fa-calendar text-xs"></i>
                <span>
                  {transportData.locationAbove?.date?.end_date
                    ? format(
                        new Date(transportData.locationAbove.date.end_date),
                        "MMMM do"
                      )
                    : "Date not set"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Travel Card - Fixed height */}
        <div className="h-full bg-white rounded-xl p-3 overflow-hidden">
          {relevantAddedFlight ? (
            <UserFlight
              relevantAddedFlight={relevantAddedFlight}
              transportData={transportData}
              onDeleteFlight={onDeleteFlight}
              setFlightModalOpen={setFlightModalOpen}
              handleStatusChange={handleStatusChange}
              isUpdating={isUpdating}
            />
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 bg-neutral-50 rounded-full flex items-center justify-center mb-2">
                <i className="fa-regular fa-plane text-neutral-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-medium text-neutral-900 mb-1">
                No Transport Selected
              </h3>
              <p className="text-xs text-neutral-500 font-light">
                Add your transportation details
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TransportContainer;
